import {Box, Typography} from '@mui/material';
import {useEffect, useState} from 'react';
import {t} from 'i18next';
import {getUserListByClientIdAndProjectId} from '../../api/ClientManagementService';
import {getClientsAsLookup} from '../../api/ReportingManagement';
import {ClientProject} from '../../types/project-type/projectDetailsType';
import {ISelectItem} from '../common/aur-select-dropdown/types';
import AurSelectDropdown from '../common/aur-select-dropdown/AurSelectDropdown';
import AurButton from '../common/aur-button/AurButton';
import AurSingleSelectDropdown from '../common/aur-single-select-dropdown/AurSingleSelectDropdown';
import AurPopup from '../common/aur-popup/AurPopup';
import SelectFile from './SelectFile';
import {CONCIERGE_FORM_TYPE} from '../../constants/constants';
import {UserList} from '../../types/types';
import {UploadConciergeReportProps} from '../../types/concierge-reports/conciergeReportsType';
import {isEmpty} from '../../utils/utils';

const emptyData = {
  id: '',
  name: '',
};

const UploadConciergeReport = (props: UploadConciergeReportProps) => {
  const {
    closePanel,
    setUploadFinalStep,
    setUploadDocumentData,
    currentStep,
    uploadDocumentData,
  } = props;

  const [client, setClient] = useState<ISelectItem>(emptyData);
  const [clientList, setClientList] = useState<ISelectItem[]>([]);
  const [project, setProject] = useState<ISelectItem>(emptyData);
  const [projects, setProjects] = useState<ISelectItem[]>([]);
  const [teamMembersList, setTeamMembersList] = useState<ISelectItem[]>([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<ISelectItem[]>(
    []
  );
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [changeMade, setChangeMade] = useState<boolean>(false);
  const [showButtonLoader, setShowButtonLoader] = useState(false);
  const [userClientsLoading, setUserClientsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (currentStep == CONCIERGE_FORM_TYPE.EDIT) {
      //code for inital edit screen
      fetchProjects(uploadDocumentData.client);
      fetchTeamMembers(
        uploadDocumentData.client.id.toString(),
        uploadDocumentData.project.id.toString()
      );
      setClient(uploadDocumentData.client);
      setProject(uploadDocumentData.project);
      setSelectedTeamMembers(uploadDocumentData.teamMembers);
    }
  }, []);

  const onClientChange = (selectedItems: ISelectItem) => {
    setClient(selectedItems);
    if (
      currentStep === CONCIERGE_FORM_TYPE.UPLOAD ||
      (currentStep === CONCIERGE_FORM_TYPE.EDIT && changeMade)
    ) {
      resetProjectTeam(selectedItems);
    }
    if (
      currentStep === CONCIERGE_FORM_TYPE.EDIT &&
      selectedItems?.id !== uploadDocumentData.client.id
    ) {
      resetProjectTeam(selectedItems);
      setChangeMade(true);
    }
  };

  const resetProjectTeam = (selectedItems: ISelectItem) => {
    setProject(emptyData);
    setProjects([]);
    setSelectedTeamMembers([]);
    setTeamMembersList([]);
    fetchProjects(selectedItems);
  };

  const fetchClients = async (searchTerm: string) => {
    setUserClientsLoading(true);
    try {
      const result = await getClientsAsLookup(searchTerm);
      const data = result.data;
      const selectedOptionExitIndex = data.findIndex(
        (item: ISelectItem) => item.id == client.id
      );
      if (selectedOptionExitIndex == -1 && !isEmpty(client.id)) {
        data.unshift(client);
        if (data.length > 11) {
          data.pop();
        }
      }
      setClientList(data);
    } catch (e) {
      setClientList([]);
    } finally {
      setUserClientsLoading(false);
    }
  };

  useEffect(() => {
    handleClientSearch('');
  }, [client]);

  useEffect(() => {
    handleProjectSearch('');
  }, [project]);

  const fetchProjects = (data: ISelectItem, searchTerm = '') => {
    if (data?.id) {
      const filteredProjects: ClientProject[] = (
        (data?.projects ?? []) as never as ClientProject[]
      ).filter((project: ClientProject) =>
        isEmpty(searchTerm)
          ? true
          : (project?.projectName ?? '')
              .toLowerCase()
              .includes((searchTerm ?? '').toLowerCase())
      );

      const projectOptions: ISelectItem[] = filteredProjects.map(
        (project: ClientProject) => ({
          id: project.projectId,
          name: project.projectName,
        })
      );

      const selectedOptionExitIndex = projectOptions.findIndex(
        (item: ISelectItem) => item.id === project.id
      );
      if (selectedOptionExitIndex === -1 && !isEmpty(project.id)) {
        projectOptions.unshift(project);
      }

      setProjects(projectOptions);
    }
  };

  const handleClientSearch = (search: string) => {
    fetchClients(search);
  };

  const handleProjectSearch = (search: string) => {
    fetchProjects(client, search);
  };

  const handleProjectSelection = (selectedProject: ISelectItem) => {
    setProject(selectedProject);
    if (
      currentStep === CONCIERGE_FORM_TYPE.UPLOAD ||
      (currentStep === CONCIERGE_FORM_TYPE.EDIT && changeMade)
    ) {
      setSelectedTeamMembers([]);
      fetchTeamMembers(client.id.toString(), selectedProject.id.toString());
    }
    if (
      currentStep === CONCIERGE_FORM_TYPE.EDIT &&
      selectedProject?.id !== uploadDocumentData.project.id
    ) {
      setSelectedTeamMembers([]);
      fetchTeamMembers(client.id.toString(), selectedProject.id.toString());
      setChangeMade(true);
    }
  };

  const handleTeamMemberChange = (selectedItems: ISelectItem[] | undefined) => {
    setSelectedTeamMembers(selectedItems ?? []);
    if (
      currentStep == CONCIERGE_FORM_TYPE.EDIT &&
      checkMembersChanged(selectedItems ?? [], uploadDocumentData.teamMembers)
    ) {
      setChangeMade(true);
    }
  };

  const checkMembersChanged = (
    array1: ISelectItem[],
    array2: ISelectItem[]
  ) => {
    if (array1.length !== array2.length) return true;

    const sortAndStringify = (arr: ISelectItem[]) =>
      arr.map((obj) => JSON.stringify(obj)).sort((a, b) => a.localeCompare(b));

    return (
      JSON.stringify(sortAndStringify(array1)) !=
      JSON.stringify(sortAndStringify(array2))
    );
  };

  const handleClearClick = () => {
    setSelectedTeamMembers([]);
    setChangeMade(true);
  };

  const fetchTeamMembers = async (clientId: string, projectId: string) => {
    try {
      if (clientId && projectId) {
        const response = await getUserListByClientIdAndProjectId(
          clientId,
          projectId
        );
        const userList = response?.data?.data?.map((item: UserList) => {
          return {
            id: item.userId,
            name: `${item.firstName} ${item.lastName}`,
          };
        });
        setTeamMembersList(userList);
      }
    } catch (error) {
      setTeamMembersList([]);
    }
  };

  const handleCancelClick = () => {
    //changes to be added to go to previous screen
    currentStep == CONCIERGE_FORM_TYPE.UPLOAD
      ? setCancelModal(true)
      : changeMade
        ? setCancelModal(true)
        : setUploadFinalStep(true);
  };

  const handleSelectFileClick = (file: File, fileName: string) => {
    setShowButtonLoader(true);
    setUploadDocumentData({
      client: client,
      project: project,
      teamMembers: selectedTeamMembers,
      file,
      fileName,
      description: uploadDocumentData?.description ?? '',
    });
    setUploadFinalStep(true);
  };

  const closeModalHandler = () => {
    if (currentStep == CONCIERGE_FORM_TYPE.UPLOAD) {
      setCancelModal(false);
      closePanel();
    } else {
      //changes for going to report details panel to be added here
      setCancelModal(false);
      setUploadFinalStep(true);
    }
  };

  const closePanelHandler = () => {
    setCancelModal(false);
  };

  return (
    <>
      <Box className="upload-content-frame" component="main">
        <Box className="upload-content-container">
          <Box className="upload-header-container">
            <Box className="upload-header">
              <Typography className="typo-text headline2_size custom-header">
                {currentStep == CONCIERGE_FORM_TYPE.UPLOAD
                  ? t('conciergeReports.uploadPanel.uploadReport')
                  : t('conciergeReports.uploadPanel.editReport')}
              </Typography>
            </Box>
            <Box className="upload-sub-header">
              <Typography className="typo-text body3_size custom-body">
                {t('conciergeReports.uploadPanel.uploadSubHeading')}
              </Typography>
            </Box>
          </Box>
          <Box className="upload-input-container">
            <Box className="input-user-info">
              <Box className="upload-form-control form-content client-input multiSelectDropdownInput">
                <AurSingleSelectDropdown
                  label={t('conciergeReports.formLabels.client')}
                  items={clientList}
                  itemKey="id"
                  itemDisplay="name"
                  dropdownLabel={t('conciergeReports.formLabels.client')}
                  search={true}
                  selectedValue={client.id != '' ? client : ''}
                  labelId={'project-group'}
                  onSelectionChange={onClientChange}
                  dataTestId="aur-test-client-dropdown"
                  onSearch={handleClientSearch}
                  searchReqLoading={userClientsLoading}
                />
              </Box>
              <Box className="upload-form-control form-content multiSelectDropdownInput">
                <AurSingleSelectDropdown
                  label={t('conciergeReports.formLabels.project')}
                  items={projects}
                  itemKey="id"
                  itemDisplay="name"
                  dropdownLabel={t('conciergeReports.formLabels.project')}
                  search={true}
                  selectedValue={project.id != '' ? project : ''}
                  disabled={client.id == ''}
                  labelId={'project-group'}
                  onSelectionChange={handleProjectSelection}
                  dataTestId="aur-test-project-dropdown"
                  helperText={
                    client.id == ''
                      ? t('conciergeReports.formLabels.projectHelpText')
                      : ''
                  }
                  helperTextClass="helper-text-upload"
                  onSearch={handleProjectSearch}
                  searchReqLoading={userClientsLoading}
                />
              </Box>
              <Box className="upload-form-control form-content">
                <AurSelectDropdown
                  itemsCountwithNoScroll={3}
                  label={t('conciergeReports.formLabels.teamMembers')}
                  items={teamMembersList}
                  itemKey="id"
                  itemDisplay="name"
                  dropdownLabel={t('conciergeReports.formLabels.teamMembers')}
                  search={true}
                  searchPlaceHolder={t('userHomePage.search')}
                  selectAll={true}
                  selectAllLabel={t('common.selectAll')}
                  submitButtonText={t('common.select')}
                  labelId={'project-group'}
                  disabled={project.id == ''}
                  defaultSelection={selectedTeamMembers}
                  onSelectionChange={handleTeamMemberChange}
                  onClearClicked={handleClearClick}
                  dataTestId="aur-test-teammembers-dropdown"
                  showCheckIcon={false}
                  helperText={
                    project.id == ''
                      ? t('conciergeReports.formLabels.teamHelpText')
                      : ''
                  }
                  helperTextClass="helper-text-upload"
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className="upload-button-grp">
          <AurButton
            classNames="drawer-close-button panel-close"
            label={t('conciergeReports.buttonsLabels.cancel')}
            type="exit"
            size="Medium"
            disabled={
              currentStep == CONCIERGE_FORM_TYPE.UPLOAD
                ? false
                : showButtonLoader
            }
            title={t('conciergeReports.buttonsLabels.cancel')}
            onClick={handleCancelClick}
          />
          <SelectFile
            handleSelect={handleSelectFileClick}
            client={client}
            project={project}
            selectedTeamMembers={selectedTeamMembers}
            currentStep={currentStep}
            uploadDocumentData={uploadDocumentData}
            changeMade={changeMade}
          />
        </Box>
      </Box>
      {cancelModal && (
        <AurPopup
          className="aur-unsaved-changes-popup"
          open={cancelModal}
          onOk={closeModalHandler}
          onCancel={closePanelHandler}
          popupTitle={
            currentStep == CONCIERGE_FORM_TYPE.UPLOAD
              ? t('common.cancelUploadPopup.title')
              : t('conciergeReports.cancelEditPopup.title')
          }
          popupHeading={
            currentStep == CONCIERGE_FORM_TYPE.UPLOAD
              ? t('common.cancelUploadPopup.heading')
              : t('conciergeReports.cancelEditPopup.heading')
          }
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={false}
          popupContent={
            currentStep == CONCIERGE_FORM_TYPE.UPLOAD
              ? t('common.cancelUploadPopup.content')
              : t('conciergeReports.cancelEditPopup.content')
          }
          labelActionCancel={
            currentStep == CONCIERGE_FORM_TYPE.UPLOAD
              ? t('common.cancelUploadPopup.buttonStay')
              : t('conciergeReports.cancelEditPopup.buttonStay')
          }
          labelActionOk={
            currentStep == CONCIERGE_FORM_TYPE.UPLOAD
              ? t('common.cancelUploadPopup.buttonYes')
              : t('conciergeReports.cancelEditPopup.buttonYes')
          }
          exitButtonHint={
            currentStep == CONCIERGE_FORM_TYPE.UPLOAD
              ? t('common.cancelUploadPopup.exitButtonHint')
              : ''
          }
          continueButtonHint={
            currentStep == CONCIERGE_FORM_TYPE.UPLOAD
              ? t('common.cancelUploadPopup.continueButtonHint')
              : ''
          }
        />
      )}
    </>
  );
};

export default UploadConciergeReport;
